import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { getSystemTypeIcon } from '@/lib/systemTypeIcons';

interface SystemsByTypeChartProps {
  data: Array<{
    tipo: string;
    cantidad: number;
    porcentaje: number;
  }>;
}

const getIconoTipo = (tipo: string) => {
  const Icon = getSystemTypeIcon(tipo);
  return <Icon className="h-4 w-4" />;
};

const getColorTipo = (tipo: string) => {
  switch (tipo) {
    case 'CCTV': return 'hsl(var(--chart-1))';
    case 'intrusión': return 'hsl(var(--chart-2))';
    case 'accesos': return 'hsl(var(--chart-3))';
    case 'networking': return 'hsl(var(--chart-4))';
    default: return 'hsl(var(--muted))';
  }
};

const chartConfig = {
  cantidad: {
    label: "Cantidad",
    color: "hsl(var(--chart-1))",
  },
};

export const SystemsByTypeChart = ({ data }: SystemsByTypeChartProps) => {
  if (!data || data.length === 0) {
    return (
      <Card className="col-span-full">
        <CardHeader>
          <CardTitle>Distribución de Sistemas por Tipo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            No hay datos de sistemas disponibles
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map(item => ({
    ...item,
    fill: getColorTipo(item.tipo)
  }));

  return (
    <div className="space-y-4">
      {/* Chart Card */}
      <Card className="col-span-full">
        <CardHeader>
          <CardTitle>Distribución de Sistemas por Tipo</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <XAxis 
                  dataKey="tipo" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar 
                  dataKey="cantidad" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Individual Type Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {data.map((item) => (
          <Card key={item.tipo}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{item.tipo}</CardTitle>
              {getIconoTipo(item.tipo)}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{item.cantidad}</div>
              <p className="text-xs text-muted-foreground">
                {item.porcentaje}% del total
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};